import { isDirectionalEffect, validDirection } from './area_effect_geometry.js';
import { bearingToMapAngle, mapAngleToBearing } from '../direction_dial.js';

export const ShapePopoverMethods = {
  _setToolbarPopover(popover, anchor = null) {
    if (!popover) return;
    if (anchor) popover.dataset.toolbarPopoverAnchorIdValue = anchor.id;
    popover.dataset.toolbarPopoverOpenValue = String(!!anchor);
    if (anchor) popover.dispatchEvent(new CustomEvent('toolbar-popover:reposition'));
    if (this.hasShapePopoverTarget && popover === this.shapePopoverTarget) this._syncMobileControls?.();
  },

  _hideShapePopover() {
    if (this.hasSelectionToolbarTarget) this.selectionToolbarTarget.hidden = true;
    if (this.hasShapePopoverTarget) this._setToolbarPopover(this.shapePopoverTarget);
  },

  _refreshShapePopover() {
    if (this.workspaceValue) return this._refreshSelectionToolbar();
    if (!this.hasShapePopoverTarget) return;
    const shape = this._selectedShapeId && this._findShapeById(this._selectedShapeId);
    if (
      this.readonlyValue ||
      this._tool !== 'select' ||
      !shape ||
      this._editingShapeId ||
      this._characterMove ||
      (this._mobileEditor && this._shapePopoverCollapsed)
    ) {
      this._hideShapePopover();
      return;
    }
    const popover = this.shapePopoverTarget;
    popover.dataset.toolbarPopoverPresentationValue = this._mobileEditor ? 'panel' : 'anchored';
    const row = Array.from(this.shapesListTarget.querySelectorAll('[data-shape-id]')).find(
      (element) => element.dataset.shapeId === shape.id
    );
    popover.querySelectorAll('[data-shape-id]').forEach((element) => {
      element.dataset.shapeId = shape.id;
    });
    this.shapeNameTarget.textContent = shape.name;
    this.shapeNameTarget.title = shape.name;
    if (this.hasCharacterMovementTarget) this.characterMovementTarget.hidden = shape.type !== 'character';
    this._refreshCharacterMovement?.();
    this.shapeColorTarget.value = shape.color;
    const measure = {
      radius: ['radiusFeet', 'Radius (feet)'],
      cube: ['sideFeet', 'Side length (feet)'],
      line: ['lengthFeet', 'Length (feet)'],
      cone: ['lengthFeet', 'Length (feet)']
    }[shape.effectType];
    this.shapeSizeTarget.hidden = !measure;
    this.shapeSizeControlTarget.hidden = !measure;
    if (measure) {
      const [property, label] = measure;
      this.shapeSizeTarget.value = shape[property];
      this.shapeSizeTarget.setAttribute('aria-label', label);
      this.shapeSizeTarget.setAttribute('aria-valuetext', `${shape[property]} feet`);
      this.shapeSizeTarget.dataset.effectProperty = property;
      this.shapeSizeLabelTarget.textContent = `${label.replace(' (feet)', '')} · ${shape[property]} ft`;
    }
    const directional = isDirectionalEffect(shape);
    this.shapeDirectionTarget.hidden = !directional;
    if (directional)
      this.shapeDirectionDialTarget.dataset.directionDialValueValue = mapAngleToBearing(shape.directionDegrees);
    this._setToolbarPopover(popover, this._mobileEditor ? this.editorTarget : row);
  },

  closeShapePopover(event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this._mobileEditor) return this.collapseMapControls();
    const id = this._selectedShapeId;
    this._deselectShape();
    Array.from(this.shapesListTarget.querySelectorAll('[data-shape-id]'))
      .find((element) => element.dataset.shapeId === id)
      ?.querySelector('button')
      ?.focus();
  },

  changeShapeSize(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (isDirectionalEffect(shape)) this.changeDirectionalEffect(event);
    else this.changeAreaEffectSize(event);
  },

  changeShapeDirection(event) {
    const shapeId = event.currentTarget?.dataset?.shapeId || this._selectedShapeId;
    if (!shapeId || !validDirection(event.detail.value)) return;
    this.changeDirectionalEffect({
      currentTarget: {
        dataset: { shapeId, effectProperty: 'directionDegrees' },
        value: String(bearingToMapAngle(event.detail.value))
      }
    });
  },

  advancedEditShape(event) {
    event.preventDefault();
    this._showEditModal(event.currentTarget.dataset.shapeId);
  }
};
